import { FC, useContext, useMemo } from "react";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import { ProjectRunConfigurationType } from "@app/projects/enumerators";
import { formatDateTime } from "@app/shared/formatDateTime";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { ZipProjectContext } from "@app/projects/zip-projects/ZipProjectContext";
import { sortDropDownItems, getZipTimeString } from "@app/projects/zip-projects/utility";
import { ZipProjectTrendChart } from "@app/projects/zip-projects/ZipProjectTrendChart";

export const ZipProjectTimesliceManagement: FC = () => {
  const projectContext = useContext(ZipProjectContext);
  const { enabledFeatureFlags } = useContext(FeatureFlagContext);
  const { state } = projectContext;

  const isTargetingEnabled = enabledFeatureFlags.has(Features.ZIP_CODE_ANALYSIS_TARGETING);
  const isTerminationEnabled = enabledFeatureFlags.has(Features.ZIP_CODE_ANALYSIS_TERMINATION);

  const filterByFeatureFlags = <T extends { isProjected: boolean; isTermed: boolean; isProjectedTermed: boolean }>(configs: T[]) =>
    configs.filter((rc) => {
      if (rc.isProjected && !isTargetingEnabled) return false;
      if (rc.isTermed && !isTerminationEnabled) return false;
      if (rc.isProjectedTermed && (!isTargetingEnabled || !isTerminationEnabled)) return false;
      return true;
    });

  const runConfigurationOptions = useMemo(() => {
    // The project gets all published and the latest unpublished or unsuccessful configuration, and we don't want unsuccessful
    const runConfigurations = [...(state.project?.projectRunConfigurations || [])];
    const indexOfFailedPrc = state.project?.projectRunConfigurations.findIndex(prc => prc.status === 'failed');
    if (indexOfFailedPrc >= 0) {
      runConfigurations.splice(indexOfFailedPrc, 1);
    }

    const eligibleConfigs = filterByFeatureFlags(
      runConfigurations.filter((rc) => rc.projectRunConfigurationType !== ProjectRunConfigurationType.DualMembershipSupplemental)
    );

    if (isTargetingEnabled || isTerminationEnabled)
    {
      return eligibleConfigs
        .sort((a, b) => sortDropDownItems(a, b))
        .map((prc) => {
          const label = getZipTimeString(prc);

          return (
            <option
              key={prc.projectRunConfigurationId}
              value={prc.projectRunConfigurationId}
              disabled={prc.projectRunConfigurationId === state.compareToRunConfiguration?.projectRunConfigurationId}
            >
              {label}
            </option>
          );
        });
    }
    else
    {
      return eligibleConfigs
        .sort((a, b) => a.dateCreated > b.dateCreated ? -1 : 1)
        .map((prc) => {
          const label = prc.isLatestPublished
            ? `Latest Published (${formatDateTime(prc.dateCreated)})`
            : !prc.datePublished ? `Unpublished (${formatDateTime(prc.dateCreated)})`
              : formatDateTime(prc.dateCreated);

          return (
            <option
              key={prc.projectRunConfigurationId}
              value={prc.projectRunConfigurationId}
              disabled={prc.projectRunConfigurationId === state.compareToRunConfiguration?.projectRunConfigurationId}
            >
              {label}
            </option>
          );
        });
    }
  }, [state.project?.projectRunConfigurations, state.runConfiguration, state.compareToRunConfiguration, isTargetingEnabled, isTerminationEnabled]);

  const compareToRunConfigurationOptions = useMemo(() => {
    const runConfigurations = [...(state.project?.projectRunConfigurations || [])];
    const indexOfFailedPrc = state.project?.projectRunConfigurations.findIndex(prc => prc.status === 'failed');
    if (indexOfFailedPrc >= 0) {
      runConfigurations.splice(indexOfFailedPrc, 1);
    }

    const eligibleConfigs = filterByFeatureFlags(
      runConfigurations.filter((rc) => rc.projectRunConfigurationType !== ProjectRunConfigurationType.DualMembershipSupplemental)
    );

    return eligibleConfigs
      .sort((a, b) => (isTargetingEnabled || isTerminationEnabled) ? sortDropDownItems(a, b) : (a.dateCreated > b.dateCreated ? -1 : 1))
      .map((prc) => {
        const label = getZipTimeString(prc);

        return (
          <option
            key={prc.projectRunConfigurationId}
            value={prc.projectRunConfigurationId}
            disabled={prc.projectRunConfigurationId === state.runConfiguration.projectRunConfigurationId}
          >
            {label}
          </option>
        );
      });
  }, [state.project?.projectRunConfigurations, state.runConfiguration, state.compareToRunConfiguration, isTargetingEnabled, isTerminationEnabled])

  if (enabledFeatureFlags.has(Features.ZIP_ANALYSIS_TRENDING)) {
    return <ZipProjectTrendChart runConfigurationOptions={runConfigurationOptions} compareToRunConfigurationOptions={compareToRunConfigurationOptions} />;
  }

  return (
    <div className="row ps-0">
      <QESSelect
        label="Results as of:"
        testId="zip-project-overview__time-slice--no-trend-chart"
        value={state.runConfiguration?.projectRunConfigurationId}
        onChange={(e) => projectContext.onChangeRunConfiguration(parseInt(e.target.value, 10))}
        disabled={state.loading}
        containerClassName="col-3"
      >
        {runConfigurationOptions}
      </QESSelect>
      {runConfigurationOptions.length > 1 && (
        <TooltipWrapper text={state.isDualMembershipMode ? "Time slice selection is disabled when comparing memberships" : ""} containerClassName="col-3">
          <QESSelect
            label="Compare Results with:"
            testId="zip-project-timeslice-management__compared-time-slice--no-trend-chart"
            value={state.compareToRunConfiguration?.projectRunConfigurationId}
            onChange={(e) => projectContext.onChangeCompareToRunConfiguration(parseInt(e.target.value, 10))}
            disabled={state.loading || state.isDualMembershipMode}
            containerClassName={`${state.isDualMembershipMode ? "col-12 ps-0" : "col-3"} ms-3`}
            allowEmpty={true}
            placeholder="None"
          >
            {compareToRunConfigurationOptions}
          </QESSelect>
        </TooltipWrapper>
      )}
    </div>
  );
}
